// BestSellerCard.js
import React, { memo, useState, useCallback } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { cartOperations } from "../../state/ducs/cart";
import { globalOperations } from "../../state/ducs/globalState";

const BestSellerCard = memo(({ productObj, t, cartItems, addToCart, showAddToCartModal, setSelectedProductVendorName }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isAddingToCart, setIsAddingToCart] = useState(false);

  const detailsClickHandler = useCallback(() => {
    setSelectedProductVendorName(productObj.vendor_name);
  }, [productObj.vendor_name, setSelectedProductVendorName]);

  const addToCartHandler = useCallback(async () => {
    if (isAddingToCart) return;
    
    setIsAddingToCart(true);
    
    try {
      // Check if other vendor products available in the cart
      const otherVendorProducts = cartItems.filter(item => 
        item.vendor_name !== productObj.vendor_name
      );

      if (otherVendorProducts.length) {
        alert("You can order single vendor product only at a time");
        return;
      }

      detailsClickHandler();
      await addToCart(productObj);
      showAddToCartModal();
    } catch (error) {
      console.error('Failed to add to cart:', error);
      alert('Failed to add product to cart. Please try again.');
    } finally {
      setIsAddingToCart(false);
    }
  }, [isAddingToCart, cartItems, productObj, detailsClickHandler, addToCart, showAddToCartModal]);

  const numberWithCommas = useCallback((x) => {
    return x?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") || "0";
  }, []);

  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
  }, []);

  const handleImageError = useCallback(() => {
    setImageError(true);
  }, []);

  const truncateText = useCallback((text, maxLength = 24) => {
    if (!text) return '';
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  }, []);

  const productImage = `${process.env.REACT_APP_PRODUCT_IMAGE_URL}/${productObj.photo}`;
  const productLink = `/productDetails/${productObj.id}`;
  const truncatedName = truncateText(productObj.name);

  return (
    <article className="best-seller-card">
      <div className="card-image-container">
        <Link onClick={detailsClickHandler} to={productLink} aria-label={`View ${productObj.name}`}>
          {!imageLoaded && !imageError && (
            <div className="image-placeholder">
              <div className="loading-spinner"></div>
            </div>
          )}
          {imageError ? (
            <div className="image-error">
              <i className="fa fa-image" aria-hidden="true"></i>
              <span>Image unavailable</span>
            </div>
          ) : (
            <img
              src={productImage}
              className={`product-image ${imageLoaded ? 'loaded' : ''}`}
              alt={productObj.name}
              onLoad={handleImageLoad}
              onError={handleImageError}
              loading="lazy"
            />
          )}
        </Link>
        
        <div className="card-overlay">
          <div className="tools">
            <button className="tool-btn" aria-label="Add to wishlist">
              <i className="fa fa-heart" aria-hidden="true"></i>
            </button>
            <button className="tool-btn" aria-label="Compare product">
              <i className="fa fa-exchange" aria-hidden="true"></i>
            </button>
            <button className="tool-btn" aria-label="Quick view">
              <i className="fa fa-search" aria-hidden="true"></i>
            </button>
          </div>
        </div>
      </div>

      <div className="card-content">
        <div className="rating">
          {[...Array(5)].map((_, i) => (
            <i key={i} className="fa fa-star" aria-hidden="true"></i>
          ))}
        </div>
        
        <h3 className="product-title">
          <Link 
            onClick={detailsClickHandler} 
            to={productLink}
            title={productObj.name}
          >
            {truncatedName}
          </Link>
        </h3>
        
        <div className="price-container">
          <span className="current-price">
            ৳{numberWithCommas(productObj.price)}
          </span>
          {productObj.original_price && productObj.original_price > productObj.price && (
            <span className="original-price">
              ৳{numberWithCommas(productObj.original_price)}
            </span>
          )}
        </div>
        
        <button 
          onClick={addToCartHandler} 
          className={`add-to-cart-btn ${isAddingToCart ? 'loading' : ''}`}
          disabled={isAddingToCart}
          aria-label={`Add ${productObj.name} to cart`}
        >
          {isAddingToCart ? (
            <>
              <div className="btn-spinner"></div>
              Adding...
            </>
          ) : (
            t("addc")
          )}
        </button>
      </div>
    </article>
  );
});

BestSellerCard.displayName = 'BestSellerCard';

const mapStateToProps = (state) => ({
  cartItems: state.cart,
});

const mapDispatchToProps = {
  addToCart: cartOperations.addToCart,
  showAddToCartModal: globalOperations.showAddToCartModal,
  setSelectedProductVendorName: globalOperations.setSelectedProductVendorName,
};

export default connect(mapStateToProps, mapDispatchToProps)(BestSellerCard);